import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Sparkles, Trash2, Pencil } from 'lucide-react'
import type { RegexRule } from '@sophia/shared-schemas'
import { Badge } from '@/components/ui/Badge'
import { Switch } from '@/components/ui/Switch'
import { cn } from '@/lib/utils'
import { classesApi, classesQueryKey } from '../api'

interface Props {
  rule: RegexRule
  goldTotal: number
  onEdit?: () => void
}

function coverageTone(hits: number, total: number): 'success' | 'warning' | 'danger' | 'neutral' {
  if (total === 0) return 'neutral'
  const ratio = hits / total
  if (ratio >= 0.8) return 'success'
  if (ratio >= 0.4) return 'warning'
  return 'danger'
}

export function RuleCard({ rule, goldTotal, onEdit }: Props) {
  const [expanded, setExpanded] = useState(false)
  const qc = useQueryClient()

  const total = rule.goldTotal || goldTotal

  const toggleMut = useMutation({
    mutationFn: () => classesApi.updateRule(rule.classId, rule.id, { enabled: !rule.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: classesQueryKey.detail(rule.classId) }),
  })

  const removeMut = useMutation({
    mutationFn: () => classesApi.removeRule(rule.classId, rule.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: classesQueryKey.detail(rule.classId) }),
  })

  return (
    <div
      className={cn(
        'rounded-md border bg-bg-surface',
        rule.enabled ? 'border-border-subtle' : 'border-border-subtle opacity-60',
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-subtle transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-fg-3 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-fg-3 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-fg-1 truncate">{rule.name}</span>
            {rule.generatedBy === 'LLM' && (
              <Sparkles className="h-3.5 w-3.5 text-accent-600 shrink-0" aria-label="Gerado pelo LLM" />
            )}
          </div>
        </div>
        <Badge tone={coverageTone(rule.goldHits, total)}>
          <span className="data-mono">
            {rule.goldHits}/{total}
          </span>
        </Badge>
        <div className="flex items-center gap-2 ml-2">
          <Switch
            size="sm"
            checked={rule.enabled}
            onCheckedChange={() => toggleMut.mutate()}
            disabled={toggleMut.isPending}
            label={rule.enabled ? 'Desativar regra' : 'Ativar regra'}
            title={rule.enabled ? 'Desativar' : 'Ativar'}
          />
          {onEdit && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onEdit()
              }}
              className="text-fg-3 hover:text-fg-1 p-1 rounded transition-colors"
              aria-label="Editar"
              title="Editar"
            >
              <Pencil className="h-3.5 w-3.5" />
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              if (confirm(`Excluir regra "${rule.name}"?`)) removeMut.mutate()
            }}
            className="text-fg-3 hover:text-danger p-1 rounded transition-colors"
            aria-label="Excluir"
            title="Excluir"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 flex flex-col gap-3 border-t border-border-subtle">
          {rule.explanation && (
            <div>
              <h4 className="text-2xs uppercase tracking-wider text-fg-3 font-semibold mb-1">Explicação</h4>
              <p className="text-sm text-fg-2 leading-relaxed">{rule.explanation}</p>
            </div>
          )}
          <div>
            <h4 className="text-2xs uppercase tracking-wider text-fg-3 font-semibold mb-1">Padrão regex</h4>
            <pre className="data-mono text-xs px-3 py-2 rounded bg-neutral-900 text-neutral-100 overflow-x-auto whitespace-pre-wrap break-all">
              /{rule.pattern}/{rule.flags}
            </pre>
          </div>
          <div className="flex items-center gap-4 text-2xs text-fg-3">
            <span>
              Peso: <span className="data-mono text-fg-2">{rule.weight}</span>
            </span>
            <span>
              Origem: <span className="data-mono text-fg-2">{rule.generatedBy}</span>
            </span>
            <span>
              Status:{' '}
              <span className="data-mono text-fg-2">{rule.enabled ? 'ATIVA' : 'DESATIVADA'}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
